import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  LuckyDrawState,
  Winner,
  WinnerQueryDto,
  ParticipateResponse,
  LuckyDraw,
  LUCKY_DRAW_ACTION_TYPES,
} from "./types";

const API_BASE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        // You might want to trigger a refresh here if needed,
        // but following the requested structure:
        token = getCookie("access_token");
      } catch (err) {
        // Handle failure
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const initialState: LuckyDrawState = {
  winners: [],
  myGifts: [],
  activeLuckyDraw: null,
  loading: {
    winners: false,
    participate: false,
    myGifts: false,
    activeLuckyDraw: false,
  },
  error: null,
  success: {
    participate: false,
  },
  message: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

// Async Thunks

/**
 * Fetch all winners or filter by query params
 */
export const fetchWinners = createAsyncThunk<
  Winner[],
  WinnerQueryDto,
  { rejectValue: string }
>(
  LUCKY_DRAW_ACTION_TYPES.FETCH_WINNERS_START,
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await api.get<Winner[]>("/winners", {
        params: queryParams,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch winners",
      );
    }
  },
);

/**
 * Fetch winners by lucky draw ID
 */
export const fetchWinnersByLuckyDraw = createAsyncThunk<
  Winner[],
  { luckyDrawId: string; page: number; limit: number },
  { rejectValue: string }
>(
  LUCKY_DRAW_ACTION_TYPES.FETCH_WINNERS_BY_LUCKY_DRAW_START,
  async ({ luckyDrawId, page, limit }, { rejectWithValue }) => {
    try {
      const response = await api.get<Winner[]>(
        `/winners/lucky-draw/${luckyDrawId}`,
        {
          params: { page, limit },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch winners for this lucky draw",
      );
    }
  },
);

/**
 * Participate in a lucky draw
 */
export const participateInLuckyDraw = createAsyncThunk<
  ParticipateResponse,
  string, // luckyDrawId
  { rejectValue: string }
>(
  LUCKY_DRAW_ACTION_TYPES.PARTICIPATE_IN_LUCKY_DRAW_START,
  async (luckyDrawId, { rejectWithValue }) => {
    try {
      const response = await api.post<ParticipateResponse>(
        `/winners/participate`,
        {},
        {
          params: { luckyDrawId },
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to participate in lucky draw",
      );
    }
  },
);

/**
 * Fetch my gifts (winners for the current user)
 */
export const fetchMyGifts = createAsyncThunk<
  Winner[],
  void,
  { rejectValue: string }
>(
  LUCKY_DRAW_ACTION_TYPES.FETCH_MY_GIFTS_START,
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<Winner[]>("/winners/my-gifts");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch my gifts",
      );
    }
  },
);

/**
 * Fetch active lucky draw
 */
export const fetchActiveLuckyDraw = createAsyncThunk<
  LuckyDraw[],
  void,
  { rejectValue: string }
>(
  LUCKY_DRAW_ACTION_TYPES.FETCH_ACTIVE_LUCKY_DRAW_START,
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<LuckyDraw[]>("/winners/active-lucky-draw");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch active lucky draw",
      );
    }
  },
);

const luckyDrawSlice = createSlice({
  name: "luckyDraw",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetParticipateSuccess: (state) => {
      state.success.participate = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch Winners
    builder
      .addCase(fetchWinners.pending, (state) => {
        state.loading.winners = true;
        state.error = null;
      })
      .addCase(
        fetchWinners.fulfilled,
        (state, action: PayloadAction<Winner[]>) => {
          state.loading.winners = false;
          state.winners = action.payload;
        },
      )
      .addCase(fetchWinners.rejected, (state, action) => {
        state.loading.winners = false;
        state.error = action.payload || "Failed to fetch winners";
      });

    // Fetch Winners By Lucky Draw
    builder
      .addCase(fetchWinnersByLuckyDraw.pending, (state) => {
        state.loading.winners = true;
        state.error = null;
      })
      .addCase(
        fetchWinnersByLuckyDraw.fulfilled,
        (state, action: PayloadAction<Winner[]>) => {
          state.loading.winners = false;
          state.winners = action.payload;
        },
      )
      .addCase(fetchWinnersByLuckyDraw.rejected, (state, action) => {
        state.loading.winners = false;
        state.error = action.payload || "Failed to fetch winners";
      });

    // Participate In Lucky Draw
    builder
      .addCase(participateInLuckyDraw.pending, (state) => {
        state.loading.participate = true;
        state.error = null;
        state.success.participate = false;
        state.message = null;
      })
      .addCase(
        participateInLuckyDraw.fulfilled,
        (state, action: PayloadAction<ParticipateResponse>) => {
          state.loading.participate = false;
          state.success.participate = true;
          state.message = action.payload.message;
        },
      )
      .addCase(participateInLuckyDraw.rejected, (state, action) => {
        state.loading.participate = false;
        state.error = action.payload || "Failed to participate";
      });

    // Fetch My Gifts
    builder
      .addCase(fetchMyGifts.pending, (state) => {
        state.loading.myGifts = true;
        state.error = null;
      })
      .addCase(
        fetchMyGifts.fulfilled,
        (state, action: PayloadAction<Winner[]>) => {
          state.loading.myGifts = false;
          state.myGifts = action.payload;
        },
      )
      .addCase(fetchMyGifts.rejected, (state, action) => {
        state.loading.myGifts = false;
        state.error = action.payload || "Failed to fetch my gifts";
      });

    // Fetch Active Lucky Draw
    builder
      .addCase(fetchActiveLuckyDraw.pending, (state) => {
        state.loading.activeLuckyDraw = true;
        state.error = null;
      })
      .addCase(
        fetchActiveLuckyDraw.fulfilled,
        (state, action: PayloadAction<LuckyDraw[]>) => {
          state.loading.activeLuckyDraw = false;
          state.activeLuckyDraw = action.payload;
        },
      )
      .addCase(fetchActiveLuckyDraw.rejected, (state, action) => {
        state.loading.activeLuckyDraw = false;
        state.error = action.payload || "Failed to fetch active lucky draw";
      });
  },
});

export const { clearError, clearMessage, resetParticipateSuccess } =
  luckyDrawSlice.actions;
export default luckyDrawSlice.reducer;
